import React, { useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const Pagination = ({ currentPage, totalPages, onPageChange, maxPagesToShow = 22 ,isLoading = false,   }) => {
  const [dynamicMaxPages, setDynamicMaxPages] = useState(maxPagesToShow);
  const [customPage, setCustomPage] = useState("");

  const safePageChange = (page) => {
  if (isLoading) return;
  if (page === currentPage) return;
  onPageChange(page);
};

  useEffect(() => {
    const updateMaxPages = () => {
      const width = window.innerWidth;
      const minPages = 5;
      const maxWidth = 1600;
      let pages = Math.floor(
        minPages + ((width / maxWidth) * (maxPagesToShow - minPages))
      );
      pages = Math.min(pages, maxPagesToShow);
      pages = Math.max(pages, minPages);
      setDynamicMaxPages(pages);
    };

    updateMaxPages();
    window.addEventListener("resize", updateMaxPages);
    return () => window.removeEventListener("resize", updateMaxPages);
  }, [maxPagesToShow]);

  const goToCustomPage = (e) => {
    if (isLoading) return;
    if (e.key === "Enter" || e.type === "click") {
      const pageNum = parseInt(customPage);
      if (pageNum >= 1 && pageNum <= totalPages && pageNum !== currentPage) {
        onPageChange(pageNum);
        setCustomPage("");
      }
    }
  };

  const leftPages = [];
  const pagesBeforeInput = Math.floor((dynamicMaxPages - 3) / 2);

  leftPages.push(1);
  if (currentPage > 4) {
    for (let i = Math.max(2, currentPage - pagesBeforeInput); i < currentPage; i++) {
      leftPages.push(i);
    }
    if (currentPage - pagesBeforeInput > 2) leftPages.push("...");
  } else {
    for (let i = 2; i <= Math.min(8, totalPages); i++) leftPages.push(i);
  }

  const rightPages = [];
  const pagesAfterInput = Math.floor((dynamicMaxPages - 3) / 2);

  if (currentPage < totalPages - 3) rightPages.push("...");
  for (let i = Math.max(currentPage + 1, totalPages - pagesAfterInput); i <= totalPages; i++) {
    rightPages.push(i);
  }

  const pageBtn = (page, isActive) =>
    isActive
      ? "bg-blue-600 text-white shadow-sm"
      : "bg-[var(--surface)] text-blue-500 border border-[var(--border)] hover:bg-[var(--accent-muted)] hover:border-blue-500/40";

  return (
    <div className="flex justify-end items-center mt-6 space-x-1 overflow-x-auto">
      {/* PREV */}
      <button
        onClick={() => safePageChange(Math.max(currentPage - 1, 1))}
        disabled={currentPage === 1 || isLoading}
        className="text-xs p-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
      >
        <FaChevronLeft />
      </button>

      {/* LEFT PAGES */}
      {leftPages.map((page, index) =>
        page === "..." ? (
          <span key={`left-ellipsis-${index}`} className="text-xs text-[var(--text-muted)] px-1">
            …
          </span>
        ) : (
          <button
            key={`left-page-${page}-${index}`}
            onClick={() => safePageChange(page)}
            disabled={isLoading}
            className={`p-1.5 text-xs px-2.5 rounded-md transition-all duration-200 ${pageBtn(page, page === currentPage)} ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {page}
          </button>
        )
      )}

      {/* CUSTOM INPUT */}
      <div className="flex items-center px-1">
        <input
          type="number"
          value={customPage}
          onChange={(e) => setCustomPage(e.target.value)}
          onKeyDown={goToCustomPage}
          placeholder="·"
          min="1"
          max={totalPages}
          className="w-12 h-7 p-1 text-xs text-center rounded
            bg-[var(--surface)] border border-[var(--border)] text-[var(--text)]
            focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500
            transition-all duration-200"
        />
      </div>

      {/* RIGHT PAGES */}
      {rightPages.map((page, index) =>
        page === "..." ? (
          <span key={`right-ellipsis-${index}`} className="text-xs text-[var(--text-muted)] px-1">
            …
          </span>
        ) : (
          <button
            key={`right-page-${page}-${index}`}
            onClick={() => safePageChange(page)}
            className={`p-1.5 text-xs px-2.5 rounded-md transition-all duration-200 ${pageBtn(page, page === currentPage)}
            ${isLoading ? "opacity-50 cursor-not-allowed" : ""}
            `}
          >
            {page}
          </button>
        )
      )}

      {/* NEXT */}
      <button
        onClick={() => safePageChange(Math.min(currentPage + 1, totalPages))}
        disabled={currentPage === totalPages || isLoading}
        className="text-xs p-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
      >
        <FaChevronRight />
      </button>

      <span className="text-xs text-[var(--text-muted)] ml-3 hidden sm:inline">
        {currentPage} / {totalPages}
      </span>
    </div>
  );
};

export default Pagination;
